/**
 * The route table.
 *
 * Every screen in the app appears here exactly once. Two rules hold it together:
 *
 * - **`/login` is the only route outside `RequireAuth`.** Everything else is
 *   inside it and therefore inside `AdminLayout`, which is what keeps the shell
 *   from being torn down and rebuilt on every navigation.
 * - **A route's guard matches its menu entry's `anyOf`** in
 *   [`layout/navigation.ts`](./layout/README.md). A visible menu item that lands
 *   on `NotPermitted` is worse than a hidden one: it tells the user the feature
 *   exists and then refuses it.
 *
 * Module pages are added from [`modules/`](./modules/README.md) through
 * `useModules()`. For them the second rule holds by construction: a module page
 * declares its guard once, and its menu entry inherits it.
 *
 * Screens are loaded eagerly. The whole admin interface is a few hundred
 * kilobytes and it is behind a sign-in — code-splitting it would trade a
 * measurable spinner on every first visit to a section for a saving nobody on an
 * internal tool will notice.
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequirePermission } from './auth/RequireAuth';
import { AdminLayout } from './layout/AdminLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import SystemPage from './pages/SystemPage';
import ProfilePage from './pages/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import LibrariesPage from './pages/files/LibrariesPage';
import BrowsePage from './pages/files/BrowsePage';
import TrashPage from './pages/files/TrashPage';
import SearchPage from './pages/files/SearchPage';
import UsersPage from './pages/people/UsersPage';
import UserDetailPage from './pages/people/UserDetailPage';
import GroupsPage from './pages/people/GroupsPage';
import GroupDetailPage from './pages/people/GroupDetailPage';
import RolesPage from './pages/people/RolesPage';
import RoleDetailPage from './pages/people/RoleDetailPage';
import PermissionsPage from './pages/people/PermissionsPage';
import AuditPage from './pages/audit/AuditPage';
import ChannelsPage from './pages/notifications/ChannelsPage';
import InboxPage from './pages/inbox/InboxPage';
import RulesPage from './pages/notifications/RulesPage';
import DeliveriesPage from './pages/notifications/DeliveriesPage';
import SchedulesPage from './pages/schedules/SchedulesPage';
import ScheduleDetailPage from './pages/schedules/ScheduleDetailPage';
import { useModules } from './modules/useModules';

export default function App() {
  // Read from context, never imported: see `modules/ModulesProvider.tsx`.
  const modules = useModules();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="system" element={<SystemPage />} />
          <Route path="profile" element={<ProfilePage />} />

          {/* Files carry no permission guard: the tree is reachable by anyone it
              has been shared with, and what they may do inside comes from each
              resource's own capabilities. */}
          <Route path="files" element={<LibrariesPage />} />
          <Route path="files/:libraryKey" element={<BrowsePage />} />
          <Route path="files/:libraryKey/:folderId" element={<BrowsePage />} />
          <Route path="trash" element={<TrashPage />} />
          <Route path="search" element={<SearchPage />} />
          {/* Behind no permission on purpose: your own notifications need a
              session, not a right. `notification.read` guards the
              administration of channels and rules, which is a different thing
              that shares a word. */}
          <Route path="inbox" element={<InboxPage />} />

          <Route element={<RequirePermission anyOf={['user.read']} />}>
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:id" element={<UserDetailPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={['group.read']} />}>
            <Route path="groups" element={<GroupsPage />} />
            <Route path="groups/:id" element={<GroupDetailPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={['role.read']} />}>
            <Route path="roles" element={<RolesPage />} />
            <Route path="roles/:id" element={<RoleDetailPage />} />
            {/* The catalogue is guarded by role.read rather than being open —
                it is only useful to somebody editing a role. */}
            <Route path="permissions" element={<PermissionsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={['audit.read']} />}>
            <Route path="audit" element={<AuditPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={['notification.read']} />}>
            <Route path="notifications/channels" element={<ChannelsPage />} />
            <Route path="notifications/rules" element={<RulesPage />} />
            <Route path="notifications/deliveries" element={<DeliveriesPage />} />
            <Route path="notifications" element={<Navigate to="/notifications/channels" replace />} />
          </Route>

          <Route element={<RequirePermission anyOf={['schedule.read']} />}>
            <Route path="schedules" element={<SchedulesPage />} />
            <Route path="schedules/:id" element={<ScheduleDetailPage />} />
          </Route>

          {/* Every module's pages. A page declares its own guard, and its menu
              entry inherits it, so the two cannot disagree. */}
          {modules.flatMap((module) =>
            (module.pages ?? []).map((page) => {
              const key = `${module.key}:${page.path}`;
              return page.anyOf ? (
                <Route key={key} element={<RequirePermission anyOf={[...page.anyOf]} />}>
                  <Route path={page.path} element={page.element} />
                </Route>
              ) : (
                <Route key={key} path={page.path} element={page.element} />
              );
            }),
          )}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
